import { MobileFriendly } from "@mui/icons-material";
import { Button } from "@mui/material";
import {
  BadgeCheck,
  Eye,
  FileCode,
  Monitor,
  Smartphone,
  Upload,
  X,
} from "lucide-react";

import React, { useRef, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import ProgressBar from "../../../global/ProgressBar";

export default function LandingPage() {
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== "text/html") {
      toast("Only .html files are allowed");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast("File size must be less than 10MB");
      return;
    }

    setHasFile(true); // <-- Mark as uploaded
    setFileSize(formatFileSize(file.size)); // MB with 2 decimals
    setFileName(file.name); // <-- Store file name
    setShowPreview(true); //<--show preview

    const reader = new FileReader();
    reader.onload = () => {
      setHtmlPreview(reader.result); // Set HTML preview
    };
    reader.readAsText(file);

    console.log("Selected file:", file);
  };

  function formatFileSize(bytes) {
    if (bytes < 1024) return bytes + " Bytes";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB";
    if (bytes < 1024 * 1024 * 1024)
      return (bytes / 1024 / 1024).toFixed(2) + " MB";
    return (bytes / 1024 / 1024 / 1024).toFixed(2) + " GB";
  }

  function toNew() {
    setHasFile(false);
    setFileName("");
    setFileSize("");
    setPreviewMode("desktop");
    setShowPreview(false);
  }

  const [htmlPreview, setHtmlPreview] = useState("");
  const [previewMode, setPreviewMode] = useState("desktop");
  const [hasFile, setHasFile] = useState(false);
  const [fileName, setFileName] = useState("");
  const [fileSize, setFileSize] = useState("");
  const [showPreview, setShowPreview] = useState(true);
  const [isUploaded, setIsUploaded] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  return (
    <div className="space-y-2 py-10">
      <Toaster />
      {/* already uploaded card */}
      {isUploaded && (
        <div className="w-full space-x-2   p-4 flex items-center justify-start bg-green-300/10 border-1 border-green-500 rounded-lg">
          <BadgeCheck className="w-8 h-8 p-1 text-green-500 bg-green-200/70 rounded-md " />
          <div className="flex flex-col justify-center">
            <span className="text-sm text-green-900 font-semibold">
              Landing Page Already Uploaded
            </span>
            <span className="text-[12px] text-green-900 font-normal ">
              You have a landing page file uploaded. You can view it in the
              preview or upload a new one.
            </span>
          </div>
        </div>
      )}

      {/* upload card */}
      {!hasFile && (
        <div className=" w-full p-6   bg-white dark:bg-slate-700 rounded-lg border-1 border-slate-400/20">
          <div className=" relative py-8 border-dotted 
          cursor-pointer hover:bg-gray-200/40 flex  flex-col items-center justify-center border-2 w-full  rounded-lg border-slate-600 dark:border-slate-100">
            <Upload className="w-14 h-14 p-3 rounded-full bg-gray-400/20 dark:bg-gray-500 text-slate-400 dark:text-slate-300" />
            <span className="text-sm text-slate-800 dark:text-slate-100 font-semibold mt-3">
              Drag & drop your HTML file here
            </span>
            <span className="text-xs mt-1 text-slate-800 dark:text-slate-100 font-normal ">
              or click to browse
            </span>
            <div className="flex items-center justify-center mt-2 space-x-2">
              <span className="text-xs mt-1 rounded-full m px-2 py-[2px] bg-slate-300/40 text-slate-800 dark:text-slate-100 font-normal">
                .html files only
              </span>
              <span className="text-xs mt-1 rounded-full m px-2 py-[2px] bg-slate-300/40 text-slate-800 dark:text-slate-100 font-normal">
                Max 10MB
              </span>
            </div>

            {/* Hidden file input */}
            <input
              type="file"
              accept=".html"
              ref={fileInputRef}
              onChange={handleFileChange}
              className="absolute inset-0 w-full h-full opacity-0  bg-amber-500 cursor-pointer"
            />
          </div>
        </div>
      )}
      {/* tips card */}
      {!hasFile && (
        <div className="w-full  mt-5 p-4 flex space-y-1 flex-col bg-blue-400/10 rounded-lg border-1 border-blue-400">
          <h1 className="text-sm font-bold text-blue-800 dark:text-blue-200 ">
            <span class="text-lg">💡</span>
            Quick Tipps
          </h1>
          <span className="text-[12px] font-normal font-sans text-blue-800 dark:text-blue-200 ">
            • Make sure your HTML file is self-contained with all CSS and JS
            inline
          </span>
          <span className="text-[12px] font-normal font-sans text-blue-800 dark:text-blue-200 ">
            • Use responsive design for better mobile experience
          </span>
          <span className="text-[12px] font-normal font-sans text-blue-800 dark:text-blue-200 ">
            • Test your landing page in the preview before uploading
          </span>
        </div>
      )}

      {/* upload to server card */}
      {hasFile && (
        <div className="w-full  rounded-lg border-1 border-gray-300/60 bg-white p-4">
          <div className="w-full h-full rounded-lg border-1 border-gray-300/60 bg-gray-200/30">
            <div className="flex items-center justify-between py-4 px-4 border-b-1 border-gray-300/60">
              <div className="flex items-start justify-start space-x-2 w-full">
                {/* icon */}
                <div class="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                  <FileCode className="text-white " />
                </div>

                <div className="flex flex-col  space-y-1 w-full pr-10 ">
                  <span className="text-sm font-bold  text-slate-900">
                    {fileName}
                  </span>
                  <span className="text-[11px] font-normal font-sans text-slate-900">
                    {fileSize + " • HTML File"}
                  </span>
                  {isUploading && (
                    <div className="w-full mt-1">
                      <ProgressBar progressPercentage={20} h={1} />
                      <span className="text-[11px] text-slate-500 ">
                        Uploading...
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col itemx-end justify-end">
                <X
                  onClick={() => toNew()}
                  className="w-7 h-7 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-500/16 cursor-pointer p-1 "
                />
                {!isUploaded && (
                  <span className=" text-[10px] rounded-full text-green-700 bg-green-300/20 border-1 border-green-800 px-2 py-1">
                    Uploaded
                  </span>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-4 p-4">
              <div
                onClick={() => {
                  setShowPreview(!showPreview);
                }}
                className="flex items-center justify-center bg-white border-1 hover:bg-slate-300/20 cursor-pointer border-gray-300/60 
            py-3 rounded-md space-x-1"
              >
                <Eye className="w-4 h-4 text-slate-800" />
                <span className="text-slate-800 text-sm font-medium ">
                  {showPreview ? "Hide Preview" : "Show Preview"}
                </span>
              </div>
              <div
                onClick={() => setIsUploaded(true)}
                className="flex items-center justify-center bg-blue-600 border-1 hover:bg-blue-500 cursor-pointer border-gray-300/60 
            py-3 rounded-md space-x-1"
              >
                <Upload className="w-4 h-4 text-white" />
                <span className="text-white text-sm font-medium ">
                  Upload to Server
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview card */}
      {showPreview && (
        <div className="w-full  mt-4 flex flex-col justify-center border-1 border-gray-500/30 rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-gray-500/30 border-b-1 ">
            <div className="flex items-center space-x-2">
              {/* icon */}
              <div class="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                <Eye className="text-white " />
              </div>
              {/* live preview */}
              <div className="flex flex-col   ">
                <span className="text-sm font-bold  text-slate-900 dark:text-slate-200">
                  Live Preview
                </span>
                <span className="text-[10px] font-normal font-sans dark:text-slate-300 text-slate-900">
                  New File Preview
                </span>
              </div>
            </div>
            <div className="flex items-center justify-start  space-x-2">
              {/* Desktop Button */}
              <div
                onClick={() => setPreviewMode("desktop")}
                className={`flex items-center justify-center rounded-lg space-x-1 px-2 py-1 cursor-pointer
      ${
        previewMode === "desktop"
          ? "bg-black text-white dark:bg-slate-200/20 dark:text-slate-200"
          : "bg-transparent text-slate-900 dark:text-slate-400"
      }`}
              >
                <Monitor className="w-[14px] h-[14px]" />
                <span className="text-xs">Desktop</span>
              </div>
              {/* Mobile Button */}
              <div
                onClick={() => setPreviewMode("mobile")}
                className={`flex items-center justify-center rounded-lg space-x-1 px-2 py-1 cursor-pointer
      ${
        previewMode === "mobile"
          ? "bg-black text-white dark:bg-slate-200/20 dark:text-slate-200"
          : "bg-transparent text-slate-900 dark:text-slate-400"
      }`}
              >
                <Smartphone className="w-[14px] h-[14px]" />
                <span className="text-xs">Mobile</span>
              </div>
            </div>
          </div>
          <div className="w-full flex items-center justify-center">
            <div
              className={`
      bg-white m-3 rounded-lg shadow-md overflow-hidden 
      ${previewMode === "desktop" ? "w-full h-[800px]" : "w-[390px] h-[700px] "}
    `}
            >
              <iframe
                srcDoc={htmlPreview}
                className="w-full h-full "
                title="HTML Preview"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
